import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { LoadingButton } from '@mui/lab';
import { Google, Lock, Mail } from '@mui/icons-material';
import { Alert, Box, Divider, Link, Stack, Typography } from '@mui/material';

import { ControlledTextField } from '@frontend/features/common/components/ControlledTextField';
import { ControlledPasswordField } from '@frontend/features/common/components/ControlledPasswordField';
import { theme } from '@frontend/theme';
import { useAuth } from '@frontend/features/auth/hooks/useAuth';
import { UserCreateDto } from '@bandido/shared/dtos';
import { AuthScreens } from '../types';
import { useGetPublicSettingsByAgencyId } from '@frontend/features/agencies/queries';
import { SettingsFields } from '@bandido/shared/types';
import { CONFIG } from '@frontend/config';
import { logo } from '@frontend/assets';

interface LoginProps {
  emailValue?: string;
  onChangeScreen: (nextPage: AuthScreens) => () => void;
  onLoginCallback: () => void;
}

const resolver = yupResolver(
  yup.object().shape({
    email: yup.string().required(),
    password: yup.string().required(),
  }),
);

export const Login: FC<LoginProps> = ({ onChangeScreen, onLoginCallback, emailValue }) => {
  const { t } = useTranslation();
  const { checkIsLogged, doSignIn } = useAuth();
  const { data: settings } = useGetPublicSettingsByAgencyId({ id: CONFIG.AGENCY_ID });
  const { handleSubmit, control, reset } = useForm({ resolver });
  const [loading, setLoading] = useState(false);
  const [loginError, setLoginError] = useState<string | undefined>(undefined);

  const onSubmit = (formData: UserCreateDto) => {
    setLoginError(undefined);
    doSignIn({
      ...formData,
      callback: (status) => {
        if (status) {
          setLoginError(t(status === 401 ? 'auth.login.error' : 'common.error.server'));
        }
        onLoginCallback();
      },
    });
  };

  const handleGoogleSignIn = () => {
    window.open(`${CONFIG.API_URL}auth/google?agencyId=${CONFIG.AGENCY_ID}`, '_self');
  };

  useEffect(() => {
    checkIsLogged();
    setLoading(false);
  }, [checkIsLogged, setLoading]);

  useEffect(() => {
    if (emailValue) {
      reset({
        email: emailValue,
      });
    }
  }, [emailValue]);

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(6),
        mt: theme.spacing(6),
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <img src={logo} alt="logo" />
      </Box>
      {emailValue && <Alert severity="success">{t('auth.register.userRegistered')}</Alert>}

      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: theme.spacing(3),
          width: '100%',
        }}
      >
        <ControlledTextField
          required
          fullWidth
          shrink
          disabled={loading}
          control={control}
          name="email"
          label={t('auth.login.email')}
          errorMessage={t('common.form.required')}
          InputProps={{
            startAdornment: <Mail color="primary" sx={{ mx: theme.spacing(1) }} />,
          }}
        />

        <ControlledPasswordField
          required
          fullWidth
          shrink
          disabled={loading}
          control={control}
          label={t('auth.login.password')}
          errorMessage={t('common.form.required')}
          name="password"
          InputProps={{
            startAdornment: <Lock color="primary" sx={{ mx: theme.spacing(1) }} />,
          }}
        />

        <LoadingButton
          loading={loading}
          fullWidth
          size="medium"
          color="primary"
          variant="contained"
          type="submit"
          sx={{
            borderRadius: theme.spacing(1),
            marginTop: theme.spacing(2),
          }}
        >
          <Typography
            sx={{ letterSpacing: '0.4px', fontWeight: (theme) => theme.typography.fontWeightMedium }}
            variant="subtitle2"
          >
            {t('auth.login.loginMesssage')}
          </Typography>
        </LoadingButton>

        {Boolean(loginError) && <Alert severity="error">{loginError}</Alert>}
      </Box>

      <Stack gap={1} textAlign="center">
        {settings?.[SettingsFields.VITE_MANUAL_REGISTRATION_ALLOWED] === 'true' && (
          <Typography
            sx={{ letterSpacing: '0.4px', fontWeight: (theme) => theme.typography.fontWeightMedium }}
            variant="subtitle2"
          >
            {t('auth.login.createAccountText')}

            <Link
              color="primary"
              sx={{
                cursor: 'pointer',
                marginLeft: theme.spacing(1),
              }}
              onClick={onChangeScreen('register')}
            >
              {t('auth.login.createAccountLink')}
            </Link>
          </Typography>
        )}
        <Typography
          sx={{ letterSpacing: '0.4px', fontWeight: (theme) => theme.typography.fontWeightMedium }}
          variant="subtitle2"
        >
          <Link
            color="primary"
            sx={{
              cursor: 'pointer',
              marginLeft: theme.spacing(1),
            }}
            onClick={onChangeScreen('forgot-password')}
          >
            {t('auth.login.forgotPassword')}
          </Link>
        </Typography>
      </Stack>

      <Divider sx={{ mb: theme.spacing(1) }} />

      <LoadingButton
        loading={loading}
        fullWidth
        size="medium"
        color="primary"
        variant="outlined"
        type="submit"
        onClick={handleGoogleSignIn}
        sx={{ borderRadius: theme.spacing(1) }}
      >
        <Typography
          sx={{
            letterSpacing: '0.4px',
            fontWeight: (theme) => theme.typography.fontWeightMedium,
            display: 'flex',
            alignItems: 'center',
          }}
          variant="subtitle2"
        >
          <Google sx={{ mr: theme.spacing(1) }} />
          {t('auth.login.loginGoogleMesssage')}
        </Typography>
      </LoadingButton>
    </Box>
  );
};
